declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

export type FlowType = "pagamento_avulso" | "pagamento_cadastrado";
export type PaymentType = "pix" | "cartao_credito";

type EcommerceEventName =
  | "view_item_list"
  | "add_to_cart"
  | "begin_checkout"
  | "add_payment_info"
  | "purchase";

interface FlowParams {
  flowType: FlowType;
}

interface ValueParams extends FlowParams {
  value: number;
}

interface PaymentParams extends ValueParams {
  paymentType: PaymentType;
}

interface PurchaseParams extends PaymentParams {
  transactionId: string;
}

const CURRENCY = "BRL";
const PURCHASE_STORAGE_KEY = "ga4_tracked_purchases";

const buildItems = (flowType: FlowType) => [
  {
    item_name: "pagamento_pedagio",
    item_category: "pedagio_digital",
    item_variant: flowType,
  },
];

const toCents = (value: number) => Math.round(value * 100) / 100;

const pushEcommerceEvent = (
  event: EcommerceEventName,
  rootFields: Record<string, unknown>,
  ecommerce: Record<string, unknown>,
) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ ecommerce: null });
  window.dataLayer.push({ event, ...rootFields, ecommerce });
};

export const trackViewItemList = ({ flowType }: FlowParams) =>
  pushEcommerceEvent(
    "view_item_list",
    { flow_type: flowType },
    {
      currency: CURRENCY,
      item_list_name: "lista_passagens_disponiveis",
      item_list_id: "pedagio_digital_passagens",
      items: buildItems(flowType),
    },
  );

export const trackAddToCart = ({ flowType, value }: ValueParams) =>
  pushEcommerceEvent(
    "add_to_cart",
    { flow_type: flowType },
    { currency: CURRENCY, value: toCents(value), items: buildItems(flowType) },
  );

export const trackBeginCheckout = ({ flowType, value }: ValueParams) =>
  pushEcommerceEvent(
    "begin_checkout",
    { flow_type: flowType },
    { currency: CURRENCY, value: toCents(value), items: buildItems(flowType) },
  );

export const trackAddPaymentInfo = ({
  flowType,
  paymentType,
  value,
}: PaymentParams) =>
  pushEcommerceEvent(
    "add_payment_info",
    { flow_type: flowType, payment_type: paymentType },
    {
      currency: CURRENCY,
      value: toCents(value),
      payment_type: paymentType,
      items: buildItems(flowType),
    },
  );

const getTrackedPurchases = (): string[] => {
  try {
    const stored = window.sessionStorage.getItem(PURCHASE_STORAGE_KEY);
    const parsed: unknown = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const markPurchaseAsTracked = (transactionId: string) => {
  try {
    window.sessionStorage.setItem(
      PURCHASE_STORAGE_KEY,
      JSON.stringify([...getTrackedPurchases(), transactionId]),
    );
  } catch {}
};

export const trackPurchase = ({
  flowType,
  paymentType,
  value,
  transactionId,
}: PurchaseParams) => {
  if (!transactionId) return;
  if (getTrackedPurchases().includes(transactionId)) return;

  markPurchaseAsTracked(transactionId);
  pushEcommerceEvent(
    "purchase",
    { flow_type: flowType, payment_type: paymentType },
    {
      transaction_id: transactionId,
      currency: CURRENCY,
      value: toCents(value),
      payment_type: paymentType,
      items: buildItems(flowType),
    },
  );
};
